"use client";

/**
 * The one "Transcribe" button, as a hook — used by the call-history panel and
 * by InsuraMentor's "From my calls" door so both behave identically. HYBRID:
 *  - capable browser (WebGPU) + call ≤30min → Whisper runs ON THE AGENT'S
 *    DEVICE (worker + ~40MB one-time cached model, zero server CPU), result
 *    saved to the shared cache;
 *  - otherwise (or on any client-side error) → the existing server job.
 * Either way the transcript is cached once per call and shown everywhere.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getProxyRecordingUrl } from "@/app/_module/lib/utils";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useToast } from "@/app/_module/hooks/use-toast";

export interface Segment {
  start: number;
  end: number;
  text: string;
}

export interface Transcript {
  id: string;
  status: "processing" | "completed" | "failed";
  segments: Segment[];
  fullText: string;
  durationSeconds: number | null;
  processedSeconds?: number;
  createdAt?: string;
  error?: string | null;
}

// Above this, background persistence beats client speed — the server keeps
// going when the agent closes the tab; a browser run would die with it.
const CLIENT_MAX_SECONDS = 1800;
// No GPU? The CPU (WASM) path still works everywhere Chrome runs — just
// slower, so cap it to calls a laptop chews through in a couple minutes.
const CLIENT_WASM_MAX_SECONDS = 600;

/** Decode any browser-supported audio (mp3/wav) to 16kHz mono Float32Array. */
async function decodeTo16kMono(buf: ArrayBuffer): Promise<Float32Array> {
  const AC: typeof AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  const probe = new AC();
  try {
    const decoded = await probe.decodeAudioData(buf.slice(0));
    const frames = Math.max(1, Math.ceil(decoded.duration * 16000));
    const off = new OfflineAudioContext(1, frames, 16000);
    const src = off.createBufferSource();
    src.buffer = decoded;
    src.connect(off.destination);
    src.start();
    const rendered = await off.startRendering();
    return Float32Array.from(rendered.getChannelData(0));
  } finally {
    void probe.close().catch(() => { /* noop */ });
  }
}

export interface ClientPhase {
  stage: "audio" | "model" | "run";
  pct?: number;
  processed?: number;
  total?: number;
}

export function useCallTranscription({
  callId,
  recordingUrl,
  durationSeconds,
  enabled = true,
}: {
  callId: string;
  /** When provided (with duration), enables the on-device hybrid path */
  recordingUrl?: string | null;
  durationSeconds?: number | null;
  /** false = don't fetch until this hook starts a transcription itself (a list of 50 rows must not fire 50 GETs). */
  enabled?: boolean;
}) {
  const { toast } = useToast();
  const workerRef = useRef<Worker | null>(null);
  const [clientPhase, setClientPhase] = useState<ClientPhase | null>(null);
  const [requested, setRequested] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  // The worker reports which model actually won the backend race — the
  // requested device can fail to init, so we must not assume.
  const modelUsedRef = useRef<string>("whisper-tiny.en");

  // Existing transcript (cached from any earlier press — by anyone on the call)
  const { data: transcript, refetch } = useQuery({
    queryKey: ["call-transcript", callId],
    queryFn: async () =>
      handleApiCall<Transcript | null>(async () =>
        api.get({ url: getApiUrl(endpoint.callTranscripts.get(callId)), auth: true }),
      ),
    enabled: enabled || requested,
    staleTime: 60_000,
    refetchInterval: (q) =>
      (q.state.data as Transcript | null)?.status === "processing" ? 2500 : false,
  });

  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const canRunClientSide = (): boolean => {
    if (typeof window === "undefined" || !recordingUrl) return false;
    // NEVER on-device in the native app: WKWebView's memory ceiling is a
    // fraction of desktop Safari's, and loading Whisper + decoding a whole
    // recording inside the webview gets the web process KILLED on longer
    // calls (WebKit termination → Capacitor error page; short calls squeak
    // by, which is why it "sometimes worked"). iOS 26 ships WebGPU, so the
    // hasWebGpu shortcut below happily green-lit the crash. Server path only.
    if (document.documentElement.classList.contains("native-app")) return false;
    if (!(window as any).Worker || !(window as any).OfflineAudioContext) return false;
    const dur = Number(durationSeconds) || 0;
    if (dur <= 0 || dur > CLIENT_MAX_SECONDS) return false;
    const forced = window.localStorage?.getItem("IC_FORCE_CLIENT_TRANSCRIBE") === "1";
    if (forced) return true;
    const hasWebGpu = !!(navigator as any).gpu;
    if (hasWebGpu) return true;
    // CPU (WASM) path for machines without WebGPU: needs a reasonable box
    // (≥4GB reported RAM, ≥4 cores) and a shorter call so it finishes fast.
    const memGb = (navigator as any).deviceMemory ?? 4;
    const cores = navigator.hardwareConcurrency ?? 4;
    return dur <= CLIENT_WASM_MAX_SECONDS && memGb >= 4 && cores >= 4;
  };

  /** Whisper on the agent's own device (WebGPU/WASM worker) → save to cache. */
  const runClientSide = async (): Promise<void> => {
    setClientPhase({ stage: "audio" });
    const proxyUrl = getProxyRecordingUrl(recordingUrl!) || recordingUrl!;
    const resolved = new URL(proxyUrl, window.location.origin);
    if (resolved.origin !== window.location.origin) throw new Error("Recording not proxyable");
    const { getSupabaseSession } = await import("@/app/_module/services/auth");
    const token = (await getSupabaseSession())?.access_token;
    if (!token) throw new Error("Not signed in");
    const resp = await fetch(resolved.toString(), { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error(`Recording fetch failed (${resp.status})`);
    const audio = await decodeTo16kMono(await resp.arrayBuffer());
    const total = audio.length / 16000;
    if (total > CLIENT_MAX_SECONDS + 60) throw new Error("Recording longer than expected");

    // navigator.gpu existing isn't enough — probe for a real adapter
    let device: "webgpu" | "wasm" = "wasm";
    try {
      if ((navigator as any).gpu && (await (navigator as any).gpu.requestAdapter())) device = "webgpu";
    } catch { /* wasm it is */ }
    const devices: Array<"webgpu" | "wasm"> = device === "webgpu" ? ["webgpu", "wasm"] : ["wasm"];
    const worker = new Worker(new URL("../components/transcribe.worker.ts", import.meta.url));
    workerRef.current = worker;
    try {
      const segments = await new Promise<Segment[]>((resolve, reject) => {
        worker.onmessage = (e: MessageEvent) => {
          const m = e.data;
          if (m?.type === "model-selected") modelUsedRef.current = m.model;
          else if (m?.type === "model") setClientPhase({ stage: "model", pct: m.pct });
          else if (m?.type === "progress") setClientPhase({ stage: "run", processed: m.processedSeconds, total });
          else if (m?.type === "done") resolve(m.segments || []);
          else if (m?.type === "error") reject(new Error(m.message));
        };
        worker.onerror = (err) => reject(new Error(err.message || "Worker failed"));
        worker.postMessage({ audio, devices }, [audio.buffer]);
      });
      await handleApiCall(async () =>
        api.post({
          url: getApiUrl(endpoint.callTranscripts.saveClient(callId)),
          body: { segments, durationSeconds: Math.round(total), model: modelUsedRef.current },
          auth: true,
        }),
      );
    } finally {
      worker.terminate();
      workerRef.current = null;
    }
  };

  const startTranscription = async () => {
    setIsStarting(true);
    try {
      if (canRunClientSide()) {
        try {
          await runClientSide();
          setRequested(true);
          setClientPhase(null);
          refetch();
          return;
        } catch (clientErr) {
          // Any on-device hiccup (model download, decode, GPU, save) falls
          // back to the server job — same button, agent never sees the seam.
          console.warn("[transcribe] client path failed, falling back to server:", clientErr);
          setClientPhase(null);
        }
      }
      await handleApiCall(async () =>
        api.post({ url: getApiUrl(endpoint.callTranscripts.request(callId)), body: {}, auth: true }),
      );
      setRequested(true);
      refetch();
    } catch (err: any) {
      setClientPhase(null);
      toast({
        title: "Couldn't Transcribe",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsStarting(false);
    }
  };

  return { transcript, refetch, clientPhase, requested, isStarting, startTranscription };
}
